import { Box, Typography, useTheme } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { object, reach, ref, string } from 'yup';
import PasswordStrengthBar from 'react-password-strength-bar';
import { useActions } from '../hooks/useActions';
import usePasswordResetToken from '../hooks/usePasswordResetToken';
import { dashboardStyle } from './style';
import { theme } from '../theme';

export const PasswordReset = ({ history }) => {
  const { login: loginPage } = dashboardStyle;
  const { t } = useTranslation();
  const passwordResetToken = usePasswordResetToken();
  const { resetPassword, sendSuccessMessage, sendErrorMessage } = useActions();
  const [isStrongPassword, setIsStrongPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  const isSubmitDisabled = !isStrongPassword || !password || validationError;

  const scoreWords = [
    t('password-reset.weak'),
    t('password-reset.medium'),
    t('password-reset.good'),
    t('password-reset.strong'),
    t('password-reset.strong'),
  ];
  const shortScoreWord = t('password-reset.too-short');

  const passwordSchema = object({
    password: string()
      .required(t('errors.validation.required'))
      .min(10, t('password-reset.too-short'))
      .matches(/[A-Z]/, t('password-reset.uppercase-letter'))
      .matches(/[0-9]/, t('password-reset.number'))
      .matches(/[^A-Za-z0-9]/, t('password-reset.symbol')),

    repeatPassword: string().oneOf(
      [ref('password')],
      t('errors.validation.password_match'),
    ),
  });

  const handleSubmit = async () => {
    setError('');
    if (password !== repeatPassword) {
      setError('password-reset.not-matched');
      return;
    }
    try {
      await resetPassword({ newPassword: password }, passwordResetToken);
      sendSuccessMessage(t('password-reset.password-successfully-changed'));
      history.push('/login');
    } catch (err) {
      const errCode = err?.response?.data?.error?.code;
      if (errCode) {
        setError(errCode);
        return;
      }
      setError('password-reset.something-went-wrong');
    }
  };

  const validateField = useCallback(
    async (fieldName, value) => {
      try {
        await reach(passwordSchema, fieldName).validate(value, {
          abortEarly: false,
        });
        setError('');
        setValidationError('');
      } catch (validationError) {
        const { message } = validationError?.inner?.[0] || {};
        setError(message);
        setValidationError(message);
      }
    },
    [passwordSchema],
  );

  const handlePasswordChange = useCallback(
    async (event) => {
      const passwordValue = event.target.value;
      if (isStrongPassword || !passwordValue) {
        setError(null);
      } else {
        setError('password-reset.too-weak');
      }
      await validateField('password', passwordValue);
      setPassword(passwordValue);
    },
    [isStrongPassword, validateField],
  );

  const handlePasswordRepeatChange = (event) => {
    setRepeatPassword(event.target.value);
  };

  return (
    <Box sx={loginPage} className='limiter reset-password'>
      <Box className='container-login100'>
        <Box className='wrap-login100'>
          <Box component='form' className='login100-form validate-form'>
            <Typography variant='body2' className='login100-form-title'>
              {t('password-reset.title')}
            </Typography>
            <Box className='input-container'>
              <TextField
                className='simple-text-field'
                label={t('forms.password')}
                name='password'
                type='password'
                autoFocus
                fullWidth
                required
                value={password}
                onChange={handlePasswordChange}
              />
            </Box>
            {password && (
              <Box className='password-strength-bar-container'>
                <PasswordStrengthBar
                  scoreWords={scoreWords}
                  shortScoreWord={shortScoreWord}
                  onChangeScore={(score) => {
                    const isStrongEnough = score >= 3;
                    setIsStrongPassword(isStrongEnough);
                  }}
                  barColors={[
                    theme?.palette.neutral.grey30,
                    theme?.palette.neutral.error,
                    theme?.palette.dashBoar.softPeach,
                    theme?.palette.myPrimary.lightBlue70,
                    theme?.palette.dashBoar.mintGreen,
                  ]}
                  password={password}
                />
              </Box>
            )}
            <Box style={{ marginTop: 0 }} className='input-container'>
              <TextField
                className='simple-text-field'
                label={t('forms.repeat-password')}
                name='repeatPassword'
                type='password'
                fullWidth
                required
                value={repeatPassword}
                onChange={handlePasswordRepeatChange}
              />
            </Box>
            <Box className='container-login100-form-btn'>
              <Box className='wrap-login100-form-btn'>
                <Box className='login100-form-bgbtn' />
                <button
                  disabled={isSubmitDisabled}
                  onClick={handleSubmit}
                  type='button'
                  className='login100-form-btn'
                >
                  {t('buttons.change-password')}
                </button>
              </Box>
            </Box>
            {error && (
              <div style={{ color: 'red', marginTop: 16 }}>{t(error)}</div>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
